import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import * as tceLocale from '@tce/tce-locale';

@Injectable({ providedIn: 'root' })
export class TceLocaleService {
  protected localeData$: Observable<{
    countries: any[];
    currencies: any[];
    languages: any[];
    timeZones: any[];
  }>;

  constructor() {
    this.localeData$ = this.loadAndCacheLocaleData();
  }

  protected loadAndCacheLocaleData(): Observable<any> {
    const countries = this.getCountries();
    const currencies = this.getCurrencies();
    const languages = this.getLanguages();
    const timeZones = this.getTimeZones();

    return forkJoin({ countries, currencies, languages, timeZones }).pipe(
      shareReplay(1), // cache the result and share with all subscribers
      catchError((err) => {
        console.error('Failed to load locale data', err);
        return of({ countries: [], currencies: [], languages: [], timeZones: [] });
      }),
    );
  }

  getLocaleData(): Observable<any> {
    return this.localeData$;
  }

  protected getCountries() {
    const data = (tceLocale as any).getCountries();
    return of(
      data.map((country) => ({
        countryCode: country.countryCode,
        countryName: country.name,
      })),
    );
  }

  protected getCurrencies() {
    const data = (tceLocale as any).getCurrencies();
    return of(
      Object.keys(data).map((currencyCode) => ({
        currencyCode,
        currencyName: data[currencyCode].name,
      })),
    );
  }

  protected getLanguages() {
    const data = (tceLocale as any).getLanguages();
    return of(
      Object.keys(data).map((language) => ({
        languageCode: language,
        languageName: data[language].name,
      })),
    );
  }

  protected getTimeZones() {
    const data = (tceLocale as any).getTimezones();
    return of(
      Object.keys(data).map((timezone) => ({
        timeZoneCode: timezone,
        timeZoneName: timezone,
        offsetUTC: data[timezone].offsetUTC,
        offsetDST: data[timezone].offsetDST,
      })),
    );
  }
}
